import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { Contact, ContactEngagement } from '../../lib/types';

/**
 * The link's enums, repeated here rather than imported.
 *
 * `@aura/shared` is built as CommonJS for the Node apps, and its barrel
 * re-exports through `__exportStar`, which ESM cannot see named exports
 * through — the web app takes only *types* from it. Keep these in step with
 * `CONTACT_RELATIONSHIP_TYPES` and `CONTACT_SENIORITY_LEVELS`; the API
 * validates against those, so a drifted value here fails as a 422.
 */
const RELATIONSHIP_TYPES = [
  'primary',
  'executive',
  'business_lead',
  'supporting',
  'stakeholder',
  'administrator',
  'technical',
  'procurement',
  'other',
] as const;

const SENIORITY_LEVELS = ['executive', 'senior', 'working'] as const;

/** One row of the engagement table: the link, the person, and the rhythm. */
type EngagementContact = ContactEngagement['contacts'][number];
import { relativeDays, titleCase } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Loading,
  SectionNote,
  Select,
  Table,
  Textarea,
  Td,
  Th,
  Tr,
  useToast,
} from '../../components/ui';

/**
 * §6.5 Contacts.
 *
 * Contacts live at the enterprise, so the same person can appear on several
 * accounts — which is exactly what §15's deterministic account matching relies
 * on when a meeting participant turns up in an email thread.
 */
export function AccountContactsTab({ accountId }: { accountId: string }) {
  const { me } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<EngagementContact | null>(null);

  const query = useQuery({
    queryKey: ['contact-engagement', accountId],
    queryFn: () => api.get<ContactEngagement>(`/accounts/${accountId}/contacts/engagement`),
  });

  const unlink = useMutation({
    mutationFn: (contactId: string) => api.delete(`/accounts/${accountId}/contacts/${contactId}`),
    onSuccess: () => {
      toast('Contact unlinked from this account');
      void queryClient.invalidateQueries({ queryKey: ['contact-engagement', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['contacts-available', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['contacts-directory'] });
      void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
    },
  });

  const contacts = query.data?.contacts ?? [];
  const coverage = query.data?.coverage ?? [];

  return (
    <>
      {coverage.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {coverage.map((gap) => (
            <div
              key={`${gap.kind}-${gap.contactId ?? ''}`}
              className="flex items-start gap-2 rounded border border-line bg-surface-2 px-3 py-2 text-xs"
            >
              <Badge tone={gap.severity === 'high' ? 'serious' : 'warning'}>
                {titleCase(gap.kind)}
              </Badge>
              <span className="text-ink-2">{gap.summary}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-muted">
          {query.data
            ? query.data.behindCadence > 0
              ? `${query.data.behindCadence} of ${contacts.length} behind their outreach rhythm`
              : `All ${contacts.length} contacts within their outreach rhythm`
            : ''}
        </span>
        {me.capabilities.canCreateWorkItems && (
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
            Add contact
          </Button>
        )}
      </div>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {query.data && contacts.length === 0 && (
        <EmptyState
          title="No contacts"
          description="Contacts are how inbound email and meetings get matched to this account."
        />
      )}

      {contacts.length > 0 && (
        <>
          <Card padded={false}>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Title</Th>
                  <Th>Email</Th>
                  <Th>Relationship</Th>
                  <Th>Seniority</Th>
                  <Th>Outreach</Th>
                  <Th align="right">Other accounts</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <Tr key={contact.id}>
                    <Td>
                      <span className="font-medium text-ink">
                        {contact.firstName} {contact.lastName}
                      </span>
                      {contact.status !== 'active' && (
                        <Badge tone="neutral" className="ml-2">
                          {titleCase(contact.status)}
                        </Badge>
                      )}
                      {contact.notes && (
                        // Two lines here, the whole note in the dialog: enough
                        // to remember who this is without pushing the row open.
                        <span
                          className="mt-0.5 line-clamp-2 max-w-64 text-[11px] text-muted"
                          title={contact.notes}
                        >
                          {contact.notes}
                        </span>
                      )}
                    </Td>
                    <Td className="text-ink-2">{contact.title ?? '—'}</Td>
                    <Td className="font-mono text-xs text-ink-2">{contact.email ?? '—'}</Td>
                    <Td>
                      <Badge
                        tone={contact.relationshipType === 'primary' ? 'info' : 'neutral'}
                        dot={false}
                      >
                        {titleCase(contact.relationshipType)}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        tone={contact.seniorityLevel === 'executive' ? 'info' : 'neutral'}
                        dot={false}
                      >
                        {titleCase(contact.seniorityLevel)}
                      </Badge>
                      <span className="mt-0.5 block text-[11px] text-muted">
                        every {contact.resolvedIntervalDays}d
                        {contact.intervalSource !== 'global' && ` (${contact.intervalSource})`}
                      </span>
                    </Td>
                    <Td className="text-xs">
                      {contact.gap ? (
                        <>
                          <Badge tone={contact.gap.severity === 'high' ? 'serious' : 'warning'}>
                            {contact.gap.overdueByDays}d overdue
                          </Badge>
                          <span className="mt-0.5 block text-[11px] text-muted">
                            {contact.gap.daysSinceOutreach === null
                              ? 'never contacted'
                              : `last ${contact.gap.daysSinceOutreach}d ago`}
                          </span>
                        </>
                      ) : contact.cadencePausedUntil ? (
                        <span className="text-muted">Paused</span>
                      ) : contact.status !== 'active' ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <span className="text-ink-2">On rhythm</span>
                      )}
                      {contact.lastResponseAt && (
                        <span className="mt-0.5 block text-[11px] text-muted">
                          replied {relativeDays(contact.lastResponseAt)}
                        </span>
                      )}
                    </Td>
                    <Td align="right" className="tnum text-xs text-muted">
                      {contact.accountCount > 1 ? contact.accountCount - 1 : '—'}
                    </Td>
                    <Td align="right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(contact)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={unlink.isPending}
                          onClick={() => unlink.mutate(contact.id)}
                        >
                          Unlink
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
          <div className="mt-3">
            <SectionNote>
              The outreach rhythm follows seniority — executives monthly, working contacts weekly —
              and can be overridden per contact. A contact who has left keeps their history and
              stops generating a rhythm; the loss shows up as a coverage gap above.
              Unlinking removes the contact from this account. The person remains on the enterprise
              and on any other accounts they are linked to.
            </SectionNote>
          </div>
        </>
      )}

      {adding && <AddContactDialog accountId={accountId} onClose={() => setAdding(false)} />}
      {editing && (
        <EditContactLinkDialog
          accountId={accountId}
          contact={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function AddContactDialog({ accountId, onClose }: { accountId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'new' | 'existing'>('new');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [relationshipType, setRelationshipType] = useState('stakeholder');
  const [businessUnit, setBusinessUnit] = useState('');
  const [existingId, setExistingId] = useState('');

  const available = useQuery({
    queryKey: ['contacts-available', accountId],
    queryFn: () => api.get<Contact[]>(`/accounts/${accountId}/contacts/available`),
  });

  const done = () => {
    toast('Contact added');
    // The table on this tab is the engagement query, not `contacts` — refreshing
    // the wrong key is what made a saved contact look like it had not saved,
    // until adding them again failed as a duplicate.
    void queryClient.invalidateQueries({ queryKey: ['contact-engagement', accountId] });
    void queryClient.invalidateQueries({ queryKey: ['contacts-available', accountId] });
    void queryClient.invalidateQueries({ queryKey: ['contacts-directory'] });
    void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
    onClose();
  };

  const create = useMutation({
    mutationFn: () =>
      api.post(`/accounts/${accountId}/contacts`, {
        firstName,
        lastName,
        email: email || null,
        title: title || null,
        relationshipType,
        businessUnit: businessUnit || null,
      }),
    onSuccess: done,
  });

  const link = useMutation({
    mutationFn: () =>
      api.post(`/accounts/${accountId}/contacts/link`, {
        contactId: existingId,
        relationshipType,
        businessUnit: businessUnit || null,
      }),
    onSuccess: done,
  });

  const pending = create.isPending || link.isPending;
  const error = create.error ?? link.error;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a contact"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              pending ||
              (tab === 'new' ? !firstName.trim() || !lastName.trim() : !existingId)
            }
            onClick={() => (tab === 'new' ? create.mutate() : link.mutate())}
          >
            {pending ? 'Saving…' : tab === 'new' ? 'Create' : 'Link'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-2">
          {(['new', 'existing'] as const).map((option) => (
            <Button
              key={option}
              size="sm"
              variant={tab === option ? 'primary' : 'secondary'}
              onClick={() => setTab(option)}
            >
              {option === 'new' ? 'New person' : 'Someone already on this enterprise'}
            </Button>
          ))}
        </div>

        {tab === 'new' ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" required>
              <Input value={firstName} onChange={(event) => setFirstName(event.target.value)} />
            </Field>
            <Field label="Last name" required>
              <Input value={lastName} onChange={(event) => setLastName(event.target.value)} />
            </Field>
            <Field
              label="Email"
              hint="Used to match inbound email and meetings to this account."
            >
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <Field label="Title">
              <Input value={title} onChange={(event) => setTitle(event.target.value)} />
            </Field>
          </div>
        ) : (
          <Field label="Person" required hint="Contacts already recorded against this enterprise.">
            <Select value={existingId} onChange={(event) => setExistingId(event.target.value)}>
              <option value="">Choose someone…</option>
              {(available.data ?? []).map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.firstName} {contact.lastName}
                  {contact.title ? ` — ${contact.title}` : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Relationship">
            <Select
              value={relationshipType}
              onChange={(event) => setRelationshipType(event.target.value)}
            >
              {RELATIONSHIP_TYPES.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Business unit">
            <Input
              value={businessUnit}
              onChange={(event) => setBusinessUnit(event.target.value)}
            />
          </Field>
        </div>

        {error && <ErrorNotice error={error} />}
      </div>
    </Dialog>
  );
}


/**
 * Editing what this account records about a person.
 *
 * Everything here belongs to the *link*, not the person: the same contact can
 * be the primary on one account and a bystander on another, and the note that
 * says why they matter here would be wrong on their other accounts. Only the
 * fields actually changed are sent, so an audit entry means something.
 */
function EditContactLinkDialog({
  accountId,
  contact,
  onClose,
}: {
  accountId: string;
  contact: EngagementContact;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [relationshipType, setRelationshipType] = useState(
    contact.relationshipType ?? 'stakeholder',
  );
  const [seniorityLevel, setSeniorityLevel] = useState(contact.seniorityLevel ?? 'working');
  const [businessUnit, setBusinessUnit] = useState(contact.businessUnit ?? '');
  const [teamBrand, setTeamBrand] = useState(contact.teamBrand ?? '');
  const [cadenceIntervalDays, setCadenceIntervalDays] = useState(
    contact.cadenceIntervalDays === null ? '' : String(contact.cadenceIntervalDays),
  );
  const [notes, setNotes] = useState(contact.notes ?? '');

  const patch: Record<string, unknown> = {};
  if (relationshipType !== (contact.relationshipType ?? 'stakeholder')) {
    patch.relationshipType = relationshipType;
  }
  if (seniorityLevel !== (contact.seniorityLevel ?? 'working')) patch.seniorityLevel = seniorityLevel;
  if (businessUnit.trim() !== (contact.businessUnit ?? '')) {
    patch.businessUnit = businessUnit.trim() || null;
  }
  if (teamBrand.trim() !== (contact.teamBrand ?? '')) patch.teamBrand = teamBrand.trim() || null;
  if (notes.trim() !== (contact.notes ?? '')) patch.notes = notes.trim() || null;
  const nextInterval = cadenceIntervalDays.trim() === '' ? null : Number(cadenceIntervalDays);
  if (nextInterval !== contact.cadenceIntervalDays) patch.cadenceIntervalDays = nextInterval;

  const intervalInvalid =
    nextInterval !== null && (!Number.isInteger(nextInterval) || nextInterval < 1 || nextInterval > 365);

  const save = useMutation({
    mutationFn: () => api.patch(`/accounts/${accountId}/contacts/${contact.id}`, patch),
    onSuccess: () => {
      toast('Contact updated');
      void queryClient.invalidateQueries({ queryKey: ['contact-engagement', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['contacts-directory'] });
      void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${contact.firstName} ${contact.lastName} on this account`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={save.isPending || Object.keys(patch).length === 0 || intervalInvalid}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {save.error && <ErrorNotice error={save.error} />}

        <Field
          label="Note"
          hint="Why this person matters here — their remit, who they answer to, how they like to be dealt with. Visible to anyone who can see this account."
        >
          <Textarea
            value={notes}
            maxLength={2000}
            rows={5}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Runs the LMS day to day. Wants agendas in advance; goes quiet in quarter-end."
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Relationship">
            <Select
              value={relationshipType}
              onChange={(event) => setRelationshipType(event.target.value)}
            >
              {RELATIONSHIP_TYPES.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Seniority" hint="Sets the expected outreach rhythm.">
            <Select
              value={seniorityLevel}
              onChange={(event) => setSeniorityLevel(event.target.value as typeof seniorityLevel)}
            >
              {SENIORITY_LEVELS.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Business unit">
            <Input value={businessUnit} onChange={(event) => setBusinessUnit(event.target.value)} />
          </Field>
          <Field label="Team or brand">
            <Input value={teamBrand} onChange={(event) => setTeamBrand(event.target.value)} />
          </Field>
          <Field
            label="Contact every (days)"
            hint={`Blank follows the ${contact.intervalSource} policy: ${contact.resolvedIntervalDays}d.`}
            error={intervalInvalid ? 'Between 1 and 365 days.' : null}
          >
            <Input
              type="number"
              min={1}
              max={365}
              value={cadenceIntervalDays}
              onChange={(event) => setCadenceIntervalDays(event.target.value)}
            />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
